// Shared pure types for the simulation. No DOM, no vendor SDKs.

export interface Vec2 {
  x: number;
  y: number;
}

/** Axis-aligned bounding box: top-left corner + size, in world units. */
export interface AABB {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type TileType = 'empty' | 'solid' | 'spike';

/** Raw per-tick input — each field is the HELD state of a control this tick.
 *  The World derives press/release edges internally (see world.ts). */
export interface InputState {
  left: boolean;
  right: boolean;
  jump: boolean;
  dash: boolean;
  // Menu-only navigation (the simulation ignores these).
  up?: boolean;
  down?: boolean;
  back?: boolean;
}

export function noInput(): InputState {
  return { left: false, right: false, jump: false, dash: false };
}

export type DeathCause = 'spike' | 'enemy' | 'pit';

/** Telemetry seam. Defined in core so the World can emit events without
 *  importing a vendor SDK. The browser wires a New Relic implementation;
 *  tests use NoopTelemetry. */
export interface GameEventMap {
  game_start: Record<string, never>;
  overworld_open: Record<string, never>;
  level_select: { level: string };
  level_start: { level: string };
  level_complete: { level: string; time_ms: number; deaths: number };
  player_death: { level: string; cause: DeathCause };
}

export interface Telemetry {
  gameEvent<K extends keyof GameEventMap>(name: K, payload: GameEventMap[K]): void;
}

export const NoopTelemetry: Telemetry = {
  gameEvent() {
    /* no-op */
  },
};

/** True if two AABBs overlap (strict — touching edges do not count). */
export function aabbOverlap(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
